import { useCallback, useMemo, useState, useRef } from 'react';
import { cleanupFiles, fetchFreshDownloadUrl, getDownloadUrl, signPdf, uploadPdf } from '../utils/api';
import type { SignerProfile, SigningField, UploadedPdfInfo } from '../types/fields';
import { buildSigningAuditManifest, persistAuditManifest } from '../utils/auditManifest';
import type { UploadProgress } from '../utils/api';
import type { SourceFileHandle } from '../utils/sourceFileAccess';
import { invalidatePageSizeSync, markPageSizeSyncAccepted, shouldAcceptPageSizeSync } from '../utils/pageSizeEpoch';
import { parsePdfUserError } from '../utils/pdfUserError';
import { validatePdfUploadFile } from '../utils/pdfUploadValidation';
import { isSignedDownloadStale } from '../utils/signingFieldState';

export interface ApplySignaturesOptions {
  signerProfile?: SignerProfile;
  saveAuditManifest?: boolean;
}

export function usePdfSession() {
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
  // IMPORTANT (Vercel / hosted mode):
  // uploadedFileId represents a *short-lived* server-side (blob) identifier.
  // The client is expected to retain the actual PDF bytes (pdfFile) for the lifetime of the editing session.
  // After page mutations or in "direct" sign flows we re-send bytes rather than relying on the id staying valid across requests.
  const [originalFileName, setOriginalFileName] = useState('');
  const [pageCount, setPageCount] = useState(0);
  const [pageSizes, setPageSizes] = useState<
    Array<{ width: number; height: number; rotation?: number; visual?: boolean }>
  >([]);
  const [signedDownloadUrl, setSignedDownloadUrl] = useState<string | null>(null);
  const [signedFileId, setSignedFileId] = useState<string | null>(null);
  /** Server-suggested filename for the signed output (from POST /sign). */
  const [signedOutputName, setSignedOutputName] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isSignedOutputStale, setIsSignedOutputStale] = useState(false);
  const [hasAcroFormFields, setHasAcroFormFields] = useState(false);

  // Large-file UX: real upload progress, cancellable operations, memory hints support
  const [uploadProgress, setUploadProgress] = useState(0);
  const [signingProgress, setSigningProgress] = useState(0);
  const [isCancelling, setIsCancelling] = useState(false);
  const [fileSize, setFileSize] = useState(0);

  const uploadAbortControllerRef = useRef<AbortController | null>(null);
  const signingAbortControllerRef = useRef<AbortController | null>(null);
  /** Synchronous guard — blocks concurrent applySignatures before isSigning state updates. */
  const signingInFlightRef = useRef(false);
  /** Monotonic counter — ignore stale upload results when a newer loadPdf starts. */
  const loadGenerationRef = useRef(0);

  /** Set when user opens a PDF via the native file picker — enables save beside source. */
  const sourceFileHandleRef = useRef<SourceFileHandle | null>(null);
  /** Latest signed-output handles — avoids stale closures in markSignedOutputStale. */
  const signedDownloadUrlRef = useRef<string | null>(null);
  const signedFileIdRef = useRef<string | null>(null);
  signedDownloadUrlRef.current = signedDownloadUrl;
  signedFileIdRef.current = signedFileId;

  const sessionRef = useRef({
    pdfFile,
    uploadedFileId,
    originalFileName,
    pageCount,
    pageSizes,
    signedDownloadUrl,
    signedFileId,
    signedOutputName,
    isSignedOutputStale,
    hasAcroFormFields,
    fileSize,
  });
  sessionRef.current = {
    pdfFile,
    uploadedFileId,
    originalFileName,
    pageCount,
    pageSizes,
    signedDownloadUrl,
    signedFileId,
    signedOutputName,
    isSignedOutputStale,
    hasAcroFormFields,
    fileSize,
  };

  const loadPdf = useCallback(async (
    file: File,
    sourceHandle?: SourceFileHandle | null
  ): Promise<UploadedPdfInfo | null> => {
    const validationError = validatePdfUploadFile(file);
    if (validationError) {
      setErrorMessage(validationError);
      return null;
    }

    // Snapshot the live session so a cancelled/failed replace can restore the prior doc.
    const live = sessionRef.current;
    const priorSession =
      live.pdfFile || live.uploadedFileId
        ? { ...live, sourceHandle: sourceFileHandleRef.current }
        : null;

    const restorePriorSession = () => {
      if (!priorSession) return;
      setPdfFile(priorSession.pdfFile);
      setUploadedFileId(priorSession.uploadedFileId);
      setOriginalFileName(priorSession.originalFileName);
      setPageCount(priorSession.pageCount);
      setPageSizes(priorSession.pageSizes);
      setSignedDownloadUrl(priorSession.signedDownloadUrl);
      setSignedFileId(priorSession.signedFileId);
      setSignedOutputName(priorSession.signedOutputName);
      setIsSignedOutputStale(priorSession.isSignedOutputStale);
      setHasAcroFormFields(priorSession.hasAcroFormFields);
      setFileSize(priorSession.fileSize);
      sourceFileHandleRef.current = priorSession.sourceHandle;
    };

    // Abort any in-flight upload/signing so parallel loads cannot corrupt session state.
    if (uploadAbortControllerRef.current && !uploadAbortControllerRef.current.signal.aborted) {
      uploadAbortControllerRef.current.abort();
    }
    if (signingAbortControllerRef.current && !signingAbortControllerRef.current.signal.aborted) {
      signingAbortControllerRef.current.abort();
    }
    signingInFlightRef.current = false;
    setIsSigning(false);
    setSigningProgress(0);
    signingAbortControllerRef.current = null;

    const generation = ++loadGenerationRef.current;
    invalidatePageSizeSync();

    setIsUploading(true);
    setErrorMessage('');
    setStatusMessage('Uploading PDF to local server...');
    // Non-zero so the progress bar + cancel affordance appear immediately (XHR may not
    // emit progress until the first chunk is sent).
    setUploadProgress(1);
    setIsCancelling(false);
    setPdfFile(file);
    setOriginalFileName(file.name);
    setPageCount(0);
    setPageSizes([]);
    setUploadedFileId(null);
    setSignedDownloadUrl(null);
    setSignedFileId(null);
    setSignedOutputName(null);
    setIsSignedOutputStale(false);
    setFileSize(file.size); // available immediately for progress MB display
    sourceFileHandleRef.current = sourceHandle ?? null;

    const controller = new AbortController();
    uploadAbortControllerRef.current = controller;

    const isAbortError = (e: unknown): boolean =>
      (e instanceof Error && (e.name === 'AbortError' || e.message.toLowerCase().includes('abort'))) ||
      (e instanceof DOMException && e.name === 'AbortError');

    try {
      const info = await uploadPdf(
        file,
        (p: UploadProgress) => {
          if (generation === loadGenerationRef.current) {
            setUploadProgress(p.percent);
          }
        },
        controller.signal
      );
      if (generation !== loadGenerationRef.current) return null;
      // Upload may finish on the wire after cancel — do not commit partial session state.
      if (controller.signal.aborted) {
        if (priorSession) {
          restorePriorSession();
        } else {
          setPdfFile(null);
          setOriginalFileName('');
          setUploadedFileId(null);
          setPageCount(0);
          setPageSizes([]);
          setHasAcroFormFields(false);
          setFileSize(0);
          sourceFileHandleRef.current = null;
        }
        setStatusMessage('Upload cancelled.');
        return null;
      }
      setUploadedFileId(info.fileId);
      setPageCount(info.pageCount || 0);
      setPageSizes(info.pageSizes || []);
      const detectedAcro = !!info.hasAcroFormFields;
      setHasAcroFormFields(detectedAcro);
      setFileSize(info.size || file.size || 0);
      const hasRotation = info.pageSizes?.some((p) => p?.rotation && p.rotation % 360 !== 0);
      const rotMsg = hasRotation ? ' (Contains rotated pages — positions are adjusted automatically.)' : '';
      const acroMsg = detectedAcro ? ' (Note: contains existing form fields — see Left panel)' : '';
      const folderMsg = sourceHandle ? ' Save will default to the same folder as this file.' : '';
      setStatusMessage(`Loaded ${info.pageCount} page(s). Ready to sign.${rotMsg}${acroMsg}${folderMsg}`);
      return info;
    } catch (err) {
      if (generation !== loadGenerationRef.current) return null;
      setUploadProgress(0);
      uploadAbortControllerRef.current = null;
      if (isAbortError(err)) {
        // Clean reset on explicit cancel; no error banner
        if (priorSession) {
          restorePriorSession();
        } else {
          setPdfFile(null);
          setOriginalFileName('');
          setUploadedFileId(null);
          setPageCount(0);
          setPageSizes([]);
          setHasAcroFormFields(false);
          setFileSize(0);
          sourceFileHandleRef.current = null;
        }
        setStatusMessage('Upload cancelled.');
        return null;
      }
      if (priorSession) {
        restorePriorSession();
      } else {
        setPdfFile(null);
        setOriginalFileName('');
        setUploadedFileId(null);
        setPageCount(0);
        setPageSizes([]);
        setHasAcroFormFields(false);
        setFileSize(0);
        sourceFileHandleRef.current = null;
      }
      setErrorMessage(parsePdfUserError(err));
      return null;
    } finally {
      if (generation === loadGenerationRef.current) {
        setIsUploading(false);
        setUploadProgress(0);
        uploadAbortControllerRef.current = null;
        setIsCancelling(false);
      }
    }
  }, []);

  const markSignedOutputStale = useCallback(() => {
    const hasSignedOutput = !!(signedDownloadUrlRef.current || signedFileIdRef.current);
    setIsSignedOutputStale(isSignedDownloadStale(hasSignedOutput, true));
  }, []);

  const applySignatures = useCallback(async (fields: SigningField[], options?: ApplySignaturesOptions) => {
    if (signingInFlightRef.current) return;
    // In Vercel / blob hosted mode the server state (fileId) is intentionally short-lived.
    // The client always retains the current working PDF bytes in `pdfFile`.
    // We therefore support "direct" signing by re-sending the File when we have it,
    // even if the previous uploadedFileId has expired or was never used for a long-lived server doc.
    if (!uploadedFileId && !pdfFile) {
      setErrorMessage('PDF was not uploaded to the local server and no local bytes available. Please re-upload the file.');
      return;
    }
    if (fields.length === 0) {
      setErrorMessage('Add at least one field before applying signatures.');
      return;
    }

    signingInFlightRef.current = true;
    setIsSigning(true);
    setErrorMessage('');
    setStatusMessage('Embedding signatures into PDF locally...');
    setSigningProgress(0);
    setIsCancelling(false);

    const controller = new AbortController();
    signingAbortControllerRef.current = controller;
    const sessionGeneration = loadGenerationRef.current;

    const isAbortError = (e: unknown): boolean =>
      (e instanceof Error && (e.name === 'AbortError' || e.message.toLowerCase().includes('abort'))) ||
      (e instanceof DOMException && e.name === 'AbortError');

    let progressInterval: number | null = null;

    try {
      // Lightweight fake staged progress for signing (server-bound; no real bytes stream)
      let prog = 6;
      setSigningProgress(prog);
      progressInterval = window.setInterval(() => {
        prog = Math.min(91, prog + 4 + Math.random() * 7);
        setSigningProgress(Math.floor(prog));
      }, 210);

      // Prefer direct bytes (pdfFile) when available. This is the recommended path for hosted/serverless
      // because it does not assume the fileId is still valid on the server after the initial upload.
      const toSign = pdfFile || uploadedFileId!;
      const response = await signPdf(toSign, fields, controller.signal);
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      if (sessionGeneration !== loadGenerationRef.current) return;
      if (controller.signal.aborted) {
        setStatusMessage('Signing cancelled.');
        return;
      }
      setSigningProgress(100);
      setSignedFileId(response.signedFileId);
      setSignedOutputName(response.originalName || null);
      // Small guard + respect server-provided downloadUrl (supports new blob storage URLs on Vercel).
      // On hosted: server may not retain the fileId long — the returned downloadUrl (or signedFileId) is the immediate way to get the result.
      const downloadUrl = response.downloadUrl || getDownloadUrl(response.signedFileId, response.originalName);
      setSignedDownloadUrl(downloadUrl);
      setIsSignedOutputStale(false);
      let status = `Signed PDF ready: ${response.originalName}`;

      if (options?.saveAuditManifest && options.signerProfile) {
        try {
          const auditDownloadUrl = await fetchFreshDownloadUrl(
            response.signedFileId,
            response.originalName
          );
          const manifest = await buildSigningAuditManifest({
            documentName: originalFileName || response.originalName || 'document.pdf',
            signedFileId: response.signedFileId,
            signedDownloadUrl: auditDownloadUrl,
            signerProfile: options.signerProfile,
            fields,
          });
          persistAuditManifest(manifest);
          status += ' Local audit record saved.';
        } catch (auditErr) {
          console.warn('Audit manifest skipped', auditErr);
        }
      }

      setStatusMessage(status);
    } catch (err) {
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      setSigningProgress(0);
      signingAbortControllerRef.current = null;
      if (isAbortError(err)) {
        setStatusMessage('Signing cancelled.');
        return;
      }
      setErrorMessage(err instanceof Error ? err.message : 'Failed to sign PDF on the local server.');
    } finally {
      if (progressInterval) clearInterval(progressInterval);
      signingInFlightRef.current = false;
      setIsSigning(false);
      setSigningProgress(0);
      signingAbortControllerRef.current = null;
      setIsCancelling(false);
    }
  }, [uploadedFileId, pdfFile, originalFileName]);

  const clearLocalFiles = useCallback(async () => {
    // Invalidate in-flight uploads so a late response cannot clobber a new session.
    loadGenerationRef.current += 1;

    if (uploadAbortControllerRef.current) {
      uploadAbortControllerRef.current.abort();
    }
    if (signingAbortControllerRef.current) {
      signingAbortControllerRef.current.abort();
    }
    uploadAbortControllerRef.current = null;
    signingAbortControllerRef.current = null;
    signingInFlightRef.current = false;

    invalidatePageSizeSync();

    // Reset session state immediately so the editor returns to upload state without
    // waiting on the cleanup network round-trip.
    setPdfFile(null);
    setUploadedFileId(null);
    setOriginalFileName('');
    setPageCount(0);
    setPageSizes([]);
    setSignedDownloadUrl(null);
    setSignedFileId(null);
    setSignedOutputName(null);
    setIsSignedOutputStale(false);
    setHasAcroFormFields(false);
    setUploadProgress(0);
    setSigningProgress(0);
    setIsUploading(false);
    setIsSigning(false);
    setIsCancelling(false);
    setFileSize(0);
    setErrorMessage('');
    sourceFileHandleRef.current = null;
    setStatusMessage('Cleared temporary files (recent files are kept).');

    try {
      await cleanupFiles();
    } catch {
      // Best-effort server purge; local session is already reset.
    }
  }, []);

  /**
   * pdf.js viewport sizes (visual space) — authoritative for field clamping in the UI.
   * @param epoch Monotonic measurement generation from usePdfDocument; stale epochs are ignored.
   */
  const syncViewerPageSizes = useCallback(
    (
      sizes: Array<{ width: number; height: number; rotation?: number }>,
      epoch: number
    ) => {
      if (!sizes.length || epoch <= 0) return;
      if (!shouldAcceptPageSizeSync(epoch)) return;
      markPageSizeSyncAccepted(epoch);
      // Mark as visual so getVisualPageSize does not double-swap on rotated pages.
      setPageSizes(sizes.map((s) => ({ ...s, visual: true })));
    },
    []
  );

  const getSourceFileHandle = useCallback(() => sourceFileHandleRef.current, []);

  /**
   * Read-only accessor for the monotonic load generation. Page operations capture
   * this before an async server round-trip and re-check it after, so a stale page-op
   * result on an OLD document cannot clobber a NEW session opened mid-flight
   * (loadPdf / clearLocalFiles both bump loadGenerationRef).
   */
  const getLoadGeneration = useCallback(() => loadGenerationRef.current, []);

  const setPdfFileFromBlob = useCallback((blob: Blob, name: string) => {
    const file = new File([blob], name || 'document.pdf', { type: 'application/pdf' });
    setPdfFile(file);
    setFileSize(file.size);
  }, []);

  /** Swap working server document after merge / page ops. */
  const applyServerDocument = useCallback(
    (
      fileId: string,
      meta: { pageCount: number; pageSizes: Array<{ width: number; height: number; rotation?: number }> }
    ) => {
      setUploadedFileId(fileId);
      setPageCount(meta.pageCount);
      // Server media dims until pdf.js remeasure completes; block stale viewport syncs.
      invalidatePageSizeSync();
      setPageSizes(meta.pageSizes);
      setSignedDownloadUrl(null);
      setSignedFileId(null);
      setSignedOutputName(null);
      setIsSignedOutputStale(true);
      // Note for Vercel Blob mode: server may not retain the fileId long (ephemeral storage during processing).
      // After page mutation you may need to treat the returned file (via fetchWorkingPdfBlob + setPdfFileFromBlob) as the new source.
      setStatusMessage(`Document updated (${meta.pageCount} pages). Re-apply before download.`);
    },
    []
  );

  const clearError = useCallback(() => setErrorMessage(''), []);

  const cancelUpload = useCallback(() => {
    const controller = uploadAbortControllerRef.current;
    if (!controller || controller.signal.aborted) return;
    setIsCancelling(true);
    controller.abort();
  }, []);

  const cancelSigning = useCallback(() => {
    if (signingAbortControllerRef.current && !signingAbortControllerRef.current.signal.aborted) {
      setIsCancelling(true);
      signingAbortControllerRef.current.abort();
    }
  }, []);

  /** Rough complexity score for memory/UX hints (pages * fields + image KB contribution). */
  const getComplexityScore = useCallback(
    (fieldCount: number, imageDataUrls: Array<string | undefined> = []): number => {
      const pages = pageCount || 1;
      let imgBytes = 0;
      for (const url of imageDataUrls) {
        if (url) {
          const comma = url.indexOf(',');
          const b64 = comma >= 0 ? url.slice(comma + 1) : url;
          imgBytes += Math.round(b64.length * 0.75);
        }
      }
      return Math.round(pages * Math.max(fieldCount, 1) + imgBytes / 1024);
    },
    [pageCount]
  );

  return useMemo(() => ({
    pdfFile,
    uploadedFileId,
    originalFileName,
    pageCount,
    pageSizes,
    signedDownloadUrl,
    signedFileId,
    signedOutputName,
    isUploading,
    isSigning,
    errorMessage,
    statusMessage,
    isSignedOutputStale,
    loadPdf,
    syncViewerPageSizes,
    getSourceFileHandle,
    getLoadGeneration,
    setPdfFileFromBlob,
    applyServerDocument,
    applySignatures,
    clearLocalFiles,
    clearError,
    markSignedOutputStale,
    setStatusMessage,
    setErrorMessage,
    hasAcroFormFields,
    // Large file UX additions (zero impact on small-file paths)
    uploadProgress,
    signingProgress,
    isCancelling,
    fileSize,
    cancelUpload,
    cancelSigning,
    getComplexityScore,
  }), [
    pdfFile,
    uploadedFileId,
    originalFileName,
    pageCount,
    pageSizes,
    signedDownloadUrl,
    signedFileId,
    signedOutputName,
    isUploading,
    isSigning,
    errorMessage,
    statusMessage,
    isSignedOutputStale,
    loadPdf,
    syncViewerPageSizes,
    getSourceFileHandle,
    getLoadGeneration,
    setPdfFileFromBlob,
    applyServerDocument,
    applySignatures,
    clearLocalFiles,
    clearError,
    markSignedOutputStale,
    setStatusMessage, // stable setState; included to satisfy exhaustive-deps lint rule
    setErrorMessage,
    hasAcroFormFields,
    uploadProgress,
    signingProgress,
    isCancelling,
    fileSize,
    cancelUpload,
    cancelSigning,
    getComplexityScore,
  ]);
}
